import { File as FileIcon, Trash2, Upload } from 'lucide-react';
import { useId, useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { Button } from '@avanta/shared/components/ui/Button';
import { cn } from '@avanta/shared/utils/cn';
import { t } from '../../i18n';
import { type FileUploadInputProps } from './types';
import { formatFileSize } from './utils';

const DEFAULT_DROP_TEXT = 'common:FileUploadInput.drop.text';
const DEFAULT_SELECT_TEXT = 'common:FileUploadInput.select.text';
const DEFAULT_HELPER_TEXT = 'common:FileUploadInput.helper.text';
const DEFAULT_REJECTION_MESSAGE = 'common:FileUploadInput.rejection.message';
const DEFAULT_ARIA_LABEL = 'common:FileUploadInput.ariaLabel';
const DEFAULT_REMOVE_FILE_ARIA_LABEL = 'common:FileUploadInput.removeFile.ariaLabel';
const DEFAULT_SEPARATOR = 'o';

export const FileUploadInput = ({
  value,
  onChange,
  onReject,
  multiple = false,
  maxFiles,
  accept,
  validator,
  maxSize,
  disabled = false,
  error,
  rejectionMessage = t(DEFAULT_REJECTION_MESSAGE),
  dropText = t(DEFAULT_DROP_TEXT),
  selectText = t(DEFAULT_SELECT_TEXT),
  helperText = t(DEFAULT_HELPER_TEXT),
  ariaLabel = t(DEFAULT_ARIA_LABEL),
  removeFileAriaLabel = t(DEFAULT_REMOVE_FILE_ARIA_LABEL, { defaultValue: 'Eliminar archivo' }),
  className,
}: FileUploadInputProps): React.ReactElement => {
  const helperId = useId();
  const [internalValue, setInternalValue] = useState<File | File[] | null>(null);
  const [rejectionError, setRejectionError] = useState<React.ReactNode>();

  const selectedFiles = ((): File[] => {
    const currentValue = value === undefined ? internalValue : value;

    if (Array.isArray(currentValue)) {
      return currentValue;
    }

    return currentValue ? [currentValue] : [];
  })();

  const hasSelectedFiles = selectedFiles.length > 0;

  const updateValue = (files: File[]): void => {
    if (value === undefined) {
      setInternalValue(multiple ? files : (files[0] ?? null));
    }

    if (multiple) {
      (onChange as ((files: File[]) => void) | undefined)?.(files);
    } else {
      (onChange as ((file: File | null) => void) | undefined)?.(files[0] ?? null);
    }
  };

  const handleOnDrop = (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
    if (acceptedFiles.length > 0) {
      const nextFiles = multiple ? [...selectedFiles, ...acceptedFiles] : [acceptedFiles[0]];
      updateValue(nextFiles);
    } else if (fileRejections.length === 0) {
      updateValue([]);
    }

    if (fileRejections.length > 0) {
      const message = fileRejections[0]?.errors[0]?.message ?? rejectionMessage;
      setRejectionError(message);
      onReject?.(fileRejections);
      return;
    }

    setRejectionError(undefined);
    onReject?.([]);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    accept,
    validator,
    maxSize,
    maxFiles: multiple ? maxFiles : 1,
    multiple,
    disabled,
    noClick: true,
    onDrop: handleOnDrop,
  });

  const errorMessage = error ?? rejectionError;
  const isInvalid = Boolean(errorMessage) || isDragReject;
  const helperContent = errorMessage || (!hasSelectedFiles ? helperText : undefined);

  const removeFile = (index: number): void => {
    updateValue(selectedFiles.filter((_, fileIndex) => fileIndex !== index));
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div
        {...getRootProps({
          role: 'group',
          'aria-label': ariaLabel,
          'aria-describedby': hasSelectedFiles && !errorMessage ? undefined : helperId,
          className: cn(
            'flex min-h-42 w-full flex-col items-center justify-center gap-2 rounded-lg border bg-gray-100 px-4 py-2 text-center transition-colors',
            'border border-dashed border-teal-400',
            isDragActive && !isInvalid && 'border-teal-600 bg-teal-100',
            isInvalid && 'border-red-400 bg-red-100',
            disabled && 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-60',
            hasSelectedFiles &&
              'bg-transparent border-none align-start justify-start p-0 text-left min-h-auto',
            className
          ),
        })}
      >
        <input {...getInputProps({ 'aria-label': ariaLabel })} />
        {hasSelectedFiles ? (
          <div className="flex w-full flex-col gap-2">
            {selectedFiles.map((file, index) => (
              <div
                className="flex min-h-10 w-full items-center gap-2 rounded-xl border border-teal-300 bg-white px-4 py-2"
                key={`${file.name}-${file.lastModified}-${index}`}
              >
                <FileIcon
                  aria-hidden="true"
                  className="size-6 shrink-0 text-teal-600"
                  strokeWidth={1.25}
                />
                <p className="min-w-0 flex-1 truncate text-sm leading-5 text-denim-500">
                  {file.name}
                </p>
                <span className="shrink-0 text-sm leading-5 text-gray-400">
                  {formatFileSize(file.size)}
                </span>
                <button
                  aria-label={`${removeFileAriaLabel}: ${file.name}`}
                  className="shrink-0 text-red-500 transition-colors hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={disabled}
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    removeFile(index);
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-5" strokeWidth={1.25} />
                </button>
              </div>
            ))}
            {errorMessage && (
              <p id={helperId} aria-live="polite" className="text-xs leading-4 text-red-400">
                {errorMessage}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Upload
                aria-hidden="true"
                className={cn('size-6 text-teal-600', isInvalid && 'text-red-400')}
                strokeWidth={1.25}
              />
              <p className="text-sm leading-5 text-denim-500">{dropText}</p>
            </div>
            <span className="text-sm leading-5 text-denim-500">{DEFAULT_SEPARATOR}</span>
            <Button type="button" variant="terciary" size="lg" disabled={disabled} onClick={open}>
              {selectText}
            </Button>
            <p id={helperId} aria-live="polite" className="text-xs leading-4 text-gray-400">
              {helperContent}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export { CSV_ACCEPT, csvFileValidator, formatFileSize } from './utils';
